import React, { Suspense } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { NavigationContainer, useNavigation } from '@react-navigation/native';
import { AppDownloadBanner } from './components/AppDownloadBanner';
import { MainScreen } from './screens/MainScreen';
import type { ScreenItem } from 'shared/src/types';
import { MarketScreens } from 'market/src/Navigator';
import { GoodsScreens } from 'goods/src/Navigator';
import { MyPageScreens } from 'mypage/src/Navigator';
import { HomeScreens, HomeTabScreens } from 'home/src/Navigator';
import { ScreensPageScreens } from 'screens/src/Navigator';
import { ModalProvider } from '@banhala/ui-kit';
import { ErrorBoundary } from 'shared/src/component';
import { LoadingView } from 'ably-app/src/components/LoadingView';
import * as Sentry from '@sentry/react';
import type { AppProps } from './App';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { BottomTabBar } from './components/BottomTabBar';

const Stack = createNativeStackNavigator();

const screenMetadata: ScreenItem[] = [
  ...MarketScreens,
  ...GoodsScreens,
  ...MyPageScreens,
  ...ScreensPageScreens,
  ...HomeScreens,
];

const BottomTabs = createBottomTabNavigator();

const BottomTabNavigator = () => {
  return (
    <BottomTabs.Navigator
      screenOptions={{ headerShown: false }}
      tabBar={props => <BottomTabBar {...props} />}
    >
      {HomeTabScreens.map((screen: ScreenItem) => (
        <BottomTabs.Screen
          name={screen.name}
          component={screen.component}
          options={{
            tabBarIcon: screen.options?.icon,
            tabBarLabel: screen.options?.tabBarLabel,
          }}
        />
      ))}
    </BottomTabs.Navigator>
  );
};

const linkingScreens = {
  tab: {
    path: '/',
    screens: HomeTabScreens.reduce<Record<string, string>>((acc, cur) => {
      acc[cur.name] = cur.path;
      return acc;
    }, {}),
  },
  ...screenMetadata.reduce<Record<string, string>>((acc, cur) => {
    acc[cur.name] = cur.path;
    return acc;
  }, {}),
};

export const NavigationWithModalProvider: React.FC<AppProps> = () => {
  return (
    <NavigationContainer
      linking={{
        config: {
          screens: linkingScreens,
        },
        prefixes: ['https://mobile.a-bly.com'],
      }}
    >
      <AsyncErrorBoundary>
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
          }}
          screenLayout={({ children }) => {
            return (
              <ModalProvider>
                <AppDownloadBanner />
                {children}
              </ModalProvider>
            );
          }}
        >
          <Stack.Screen
            options={{
              headerShown: false,
            }}
            name={'tab'}
            component={BottomTabNavigator}
          />
          <Stack.Screen name={'main'} component={MainScreen} />
          {screenMetadata.map(screen => {
            return (
              <Stack.Screen
                name={screen.name}
                component={screen.component}
                options={screen.options}
              />
            );
          })}
        </Stack.Navigator>
      </AsyncErrorBoundary>
    </NavigationContainer>
  );
};

const AsyncErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigator = useNavigation();
  return (
    <ErrorBoundary
      navigation={navigator}
      onReportError={(error, errorInfo) => {
        Sentry.captureException(error, {
          contexts: { react: { componentStack: errorInfo.componentStack } },
        });
      }}
    >
      <Suspense fallback={<LoadingView />}>{children}</Suspense>
    </ErrorBoundary>
  );
};
